import { Coords } from "types/app";

/**
 * A place on the track between two of its points: on the segment from the point `index` to the next one,
 * `fraction` of the way (0 at the point, 1 at the next one). The last point is the end of the last segment.
 */
export type TrackPosition = { index: number; fraction: number };

/** A point of the chart: distance (km) or time (s) from the start, and a value (null or undefined in gaps) */
type ChartPoint = { x: number; y: number | null | undefined };

const lerp = (a: number, b: number, fraction: number) => a + (b - a) * fraction;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * The place of the chart's horizontal value (distance or time) on the track, its points' values only grow.
 * A value before the start or after the end is the start or the end.
 */
export const findTrackPositionAt = (
  points: ChartPoint[],
  x: number
): TrackPosition => {
  if (points.length < 2 || x <= points[0].x) return { index: 0, fraction: 0 };
  const last = points.length - 1;
  if (x >= points[last].x) return { index: last - 1, fraction: 1 };

  // The last point at or before the value
  let low = 0;
  let high = last;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (points[middle].x <= x) low = middle;
    else high = middle;
  }
  const length = points[low + 1].x - points[low].x;
  return {
    index: low,
    fraction: length > 0 ? clamp((x - points[low].x) / length, 0, 1) : 0,
  };
};

/**
 * The chart's point at the place: both values in between the segment's points. In a gap of the data (a point
 * without a value) it's the value of the nearer point, which may be the gap.
 */
export const interpolateChartPoint = (
  points: ChartPoint[],
  { index, fraction }: TrackPosition
): ChartPoint => {
  const from = points[index];
  const to = points[index + 1] ?? from;
  const x = lerp(from.x, to.x, fraction);
  if (
    from.y === null ||
    from.y === undefined ||
    to.y === null ||
    to.y === undefined
  ) {
    return { x, y: fraction < 0.5 ? from.y : to.y };
  }
  return { x, y: lerp(from.y, to.y, fraction) };
};

/** The coordinates of the place, on the straight line between the segment's points */
export const interpolateCoords = (
  trackpoints: Coords[],
  { index, fraction }: TrackPosition
): Coords => {
  const from = trackpoints[index];
  const to = trackpoints[index + 1] ?? from;
  return [lerp(from[0], to[0], fraction), lerp(from[1], to[1], fraction)];
};
